import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

/* ApologeticsScriptureBanner — thin slim banner under the SiteNav that
   rotates through 30 apologetics-leaning Bible verses. One verse per page
   load (random pick). Auto-cycles every 14 seconds for visitors who stay
   on a page long enough.

   Mike's intent: facts-first faith messaging. Not preachy, not churchy.
   These verses lean toward apologetics — defending faith with evidence,
   reason, and truth — rather than devotional / prayer language. */

const VERSES = [
  { ref: "1 Peter 3:15",   text: "Always be prepared to give an answer to everyone who asks you to give the reason for the hope that you have." },
  { ref: "John 8:32",      text: "Then you will know the truth, and the truth will set you free." },
  { ref: "Romans 1:20",    text: "God's invisible qualities have been clearly seen, being understood from what has been made, so people are without excuse." },
  { ref: "Isaiah 1:18",    text: "Come now, let us reason together, says the Lord." },
  { ref: "Acts 17:11",     text: "They examined the Scriptures every day to see if what Paul said was true." },
  { ref: "Proverbs 18:17", text: "The one who states his case first seems right, until the other comes and examines him." },
  { ref: "2 Timothy 2:15", text: "Do your best to present yourself to God as one approved, a worker who correctly handles the word of truth." },
  { ref: "Colossians 2:8", text: "See to it that no one takes you captive through hollow and deceptive philosophy." },
  { ref: "Hebrews 11:1",   text: "Now faith is confidence in what we hope for and assurance about what we do not see." },
  { ref: "John 1:1",       text: "In the beginning was the Word, and the Word was with God, and the Word was God." },
  { ref: "John 14:6",      text: "Jesus answered, I am the way and the truth and the life. No one comes to the Father except through me." },
  { ref: "Psalm 19:1",     text: "The heavens declare the glory of God; the skies proclaim the work of his hands." },
  { ref: "Romans 12:2",    text: "Do not conform to the pattern of this world, but be transformed by the renewing of your mind." },
  { ref: "2 Corinthians 10:5", text: "We demolish arguments and every pretension that sets itself up against the knowledge of God." },
  { ref: "Jude 1:3",       text: "Contend for the faith that was once for all entrusted to God's holy people." },
  { ref: "Matthew 22:37",  text: "Love the Lord your God with all your heart and with all your soul and with all your mind." },
  { ref: "Psalm 14:1",     text: "The fool says in his heart, There is no God." },
  { ref: "Acts 1:3",       text: "After his suffering, he presented himself to them and gave many convincing proofs that he was alive." },
  { ref: "1 Corinthians 15:14", text: "If Christ has not been raised, our preaching is useless and so is your faith." },
  { ref: "2 Peter 1:16",   text: "We did not follow cleverly devised stories when we told you about Jesus Christ — we were eyewitnesses of his majesty." },
  { ref: "Romans 10:17",   text: "Faith comes from hearing the message, and the message is heard through the word about Christ." },
  { ref: "Psalm 119:160",  text: "All your words are true; all your righteous laws are eternal." },
  { ref: "Genesis 1:1",    text: "In the beginning God created the heavens and the earth." },
  { ref: "John 20:31",     text: "These are written that you may believe that Jesus is the Messiah, the Son of God, and that by believing you may have life in his name." },
  { ref: "Luke 1:1-4",     text: "I have carefully investigated everything from the beginning... so that you may know the certainty of the things you have been taught." },
  { ref: "Proverbs 1:7",   text: "The fear of the Lord is the beginning of knowledge." },
  { ref: "Isaiah 41:23",   text: "Tell us what the future holds, so we may know that you are gods." },
  { ref: "Daniel 12:4",    text: "Many will go here and there to increase knowledge." },
  { ref: "Ecclesiastes 12:13", text: "Now all has been heard; here is the conclusion of the matter: Fear God and keep his commandments." },
  { ref: "Hebrews 6:18",   text: "It is impossible for God to lie." },
];

/* Pick a random index that doesn't repeat the immediately previous one. */
function pickNext(prev, n) {
  if (n <= 1) return 0;
  let i = Math.floor(Math.random() * n);
  if (i === prev) i = (i + 1) % n;
  return i;
}

export default function ApologeticsScriptureBanner() {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * VERSES.length));
  const [fade, setFade] = useState(true);

  useEffect(() => {
    /* Auto-rotate every 14 seconds with a brief fade. Long enough for
       seniors to actually read the verse before it changes. */
    const t = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIdx((cur) => pickNext(cur, VERSES.length));
        setFade(true);
      }, 350);
    }, 14000);
    return () => clearInterval(t);
  }, []);

  const v = VERSES[idx];
  return (
    <div className="w-full" data-testid="apologetics-scripture-banner"
         style={{ background: "linear-gradient(90deg, #0F172A 0%, #2A1F1A 50%, #0F172A 100%)" }}>
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-3">
        <Sparkles className="w-4 h-4 flex-shrink-0" style={{ color: "#F39A1F" }}/>
        <div
          className="flex-1 min-w-0 text-[12px] md:text-[13px] leading-snug transition-opacity duration-300"
          style={{ color: "#E0F2FE", opacity: fade ? 1 : 0 }}
          data-testid="apologetics-verse"
        >
          <span className="italic">&ldquo;{v.text}&rdquo;</span>
          <span className="opacity-70 ml-2 font-bold tracking-wider uppercase text-[10px] md:text-[11px]">— {v.ref}</span>
        </div>
        <span className="hidden sm:inline-block text-[10px] tracking-[0.28em] uppercase font-bold flex-shrink-0"
              style={{ color: "#F39A1F" }}>
          ★ FAITH
        </span>
      </div>
    </div>
  );
}
